import { api } from "./client";
import { noteLunchServerTime } from "../lib/lunchClock";
import { getEdgeBaseUrl } from "../lib/edge/edgeState";
import type { LunchCounters } from "../lib/lunchCatalog";

// Translation type (used by multiple entities)
export interface Translation {
  id: number;
  languageCode: string;
  name: string;
}

// ItemLang type (specific for items)
export interface ItemLang {
  id: number;
  languageCode: string;
  itemId: number;
  name: string;
  description: string | null;
  unitAndSize: string | null;
}

// Item type
export interface Item {
  id: number;
  slug: string;
  pluCode: number | null;
  kcal: number | null;
  images: string | null;
  sortIndex: number | null;
  prepareTime: number | null;
  dds: number | null;
  categoryId: number | null;
  enabled: boolean;
  // Станциите на продаваемия — ВИНАГИ непразен списък (продукт без станция не
  // може да се маркира; org админът изисква станция).
  stationIds: number[];
  productTypeId: number | null;
  mymenu: boolean;
  delivery: boolean;
  kiosk: boolean;
  display: boolean;
  createdAt: string;
  updatedAt: string;
  price: number;
  lockedPrice: boolean;
  menuId: number;
  menuPriority: number | null;
  itemLangs: ItemLang[];
  allergenIds: number[];
  tagIds: number[];
  addonIds: number[];
  modifierIds: number[];
  // Само за копията от обедно меню (виж lib/lunchCatalog.ts): `id` е
  // синтетичен (отрицателен), истинският артикул/рецепта е `sourceItemId`,
  // а редът в сметката носи `lunchMenuId`.
  lunchMenuId?: number;
  sourceItemId?: number;
  // Името на менюто и мястото на цвета му (`lib/lunchMenuColors.ts`) — за
  // етикета в каталога вместо общото „Дневно".
  lunchMenuName?: string;
  lunchMenuColorIndex?: number;
  // Порциите, които остават днес от обедното меню (дневен лимит − продадени);
  // null/липсва = без ограничение. Без кошницата на този терминал.
  lunchRemaining?: number | null;
}

// Category type
export interface Category {
  id: number;
  slug: string;
  parentId: number | null;
  sortIndex: number | null;
  images: string | null;
  isActive: boolean;
  // Hex цвят на групата (#abcabc, org колона categories.color, 0.0.234);
  // null → „Маркиране" взема цвета на ПРЕКИЯ родител (само едно ниво);
  // и той без цвят → неутрална (без измислен цвят). Изключение: групата
  // „Обедно меню" (строи се във FE, lib/lunchCatalog.ts) носи CSS променлива
  // `var(--color-pos-lunch)` — цветът се ползва само като CSS стойност.
  color: string | null;
  translations: Translation[];
}

// Allergen type
export interface Allergen {
  id: number;
  slug: string;
  image: string | null;
  translations: Translation[];
}

// Tag type
export interface Tag {
  id: number;
  slug: string;
  image: string | null;
  type: string | null;
  translations: Translation[];
}

// Station type
export interface Station {
  id: number;
  slug: string;
  color: string | null;
  sortIndex: number | null;
  translations: Translation[];
}

// Vat type
export interface Vat {
  id: number;
  vatName: string;
  vatRate: number;
}

// AddonItem type
export interface AddonItem {
  itemId: number;
  addonItemPrice: number | null;
  price: number;
}

// Addon type
export interface Addon {
  id: number;
  slug: string;
  note: string | null;
  isActive: boolean;
  maxQty: number | null;
  // 1 = самостоятелно количество · 2 = задължителна · 3 = обикновена.
  // BE праща ЧИСЛО — сравнявай с числа, без String() обвивки.
  type: number | null;
  itemId: number | null;
  categoryId: number | null;
  translations: Translation[];
  items: AddonItem[];
}

// Modifier type
export interface Modifier {
  id: number;
  slug: string;
  note: string | null;
  isActive: boolean;
  // при true продукт с този модификатор отваря „Промени" (EditModifiersModal) при маркиране
  isRequired: boolean;
  translations: Translation[];
}

// Продукт в обедно меню — реферира артикула/рецептата от `items` по id
export interface LunchMenuCatalogItem {
  itemId: number;
  price: number;
  // {"bg": "300 г"}; липсващ език → unitAndSize на продукта
  unitAndSizeLangs: Record<string, string> | null;
  // Порции на ден (0 = без ограничение) и продадените днес към `serverTime`
  // (касата офлайн добавя своите). Липсват при огледало отпреди лимита.
  dailyLimit?: number;
  soldToday?: number;
}

// Порциите на добавка (опция) в дневно меню — ред без цена; `soldToday` —
// продадените днес от това меню (добавката върху редовете му)
export interface LunchAddonLimitCatalog {
  itemId: number;
  dailyLimit: number;
  soldToday?: number;
}

// Обедно меню (днешно или предстоящо) с графика си — „Маркиране" го показва
// като отделна група, докато графикът покрива часовника (lib/lunchCatalog.ts)
export interface LunchMenuCatalog {
  id: number;
  name: string;
  dateFrom: string | null;
  dateTo: string | null;
  // 0 = неделя … 6 = събота; null → всеки ден
  weekdays: number[] | null;
  // "ЧЧ:ММ"; „до" е включително до края на минутата
  timeFrom: string | null;
  timeTo: string | null;
  items: LunchMenuCatalogItem[];
  // Липсва при огледало/BE отпреди порциите на добавките → []
  addonLimits?: LunchAddonLimitCatalog[];
}

// Menu Response type
export interface MenuResponse {
  items: Item[];
  categories: Category[];
  allergens: Allergen[];
  tags: Tag[];
  stations: Station[];
  vats: Vat[];
  addons: Addon[];
  modifiers: Modifier[];
  // Обедните менюта, които още не са свършили — активното се избира на FE
  lunchMenus?: LunchMenuCatalog[];
  // Часовникът на сървъра при отговора (lib/lunchClock.ts)
  serverTime?: string | null;
  // САМО на FE (не е в тялото): версията от хедъра X-Menu-Version — с нея
  // проверката в „Маркиране" пита малкото GET /menu-state (hooks/menuStateCheck.ts)
  menuVersion?: string;
  // САМО на FE: броячите на порциите на активните дневни менюта (withLunchMenus)
  lunchCounters?: LunchCounters;
}

// Продадените днес порции на обеден продукт с дневен лимит
export interface LunchSold {
  lunchMenuId: number;
  itemId: number;
  soldToday: number;
}

// GET /menu-state: версията на цялото GET /menu/ (без serverTime и soldToday),
// часовникът на сървъра и обедните броячи
export interface MenuStateResponse {
  version: string;
  serverTime: string | null;
  lunchSold: LunchSold[];
}

const MENU_VERSION_HEADER = "x-menu-version";

/**
 * Fetch menu for a specific restaurant
 * @param restaurantName - Name of the restaurant
 * @returns Menu data with all related entities
 */
export const getMenu = async (
  restaurantName: string,
): Promise<MenuResponse> => {
  // Версията се иска само без локален резерв: само тогава проверката ползва
  // GET /menu-state; с резерв заявката (и огледалото към касата) е както преди
  const withVersion = getEdgeBaseUrl() == null;
  const response = await api.get<MenuResponse>("/menu/", {
    params: withVersion
      ? { restaurant_name: restaurantName, with_version: true }
      : { restaurant_name: restaurantName },
  });
  // Часовникът на обедните менюта се сверява само по облака — копието в
  // касата (офлайн) носи времето, когато е записано
  const fromCloud = !response.config.edgeLocal && !response.config.edgeRetried;
  if (fromCloud) noteLunchServerTime(response.data?.serverTime);
  const menuVersion = response.headers?.[MENU_VERSION_HEADER];
  // Копие, не промяна: огледалото на касата вече е взело `response.data`
  return withVersion && fromCloud && typeof menuVersion === "string" && menuVersion
    ? { ...response.data, menuVersion }
    : response.data;
};

/**
 * Малката проверка на менюто (`hooks/menuStateCheck.ts`) — само без локален
 * резерв; касата я няма.
 */
export const getMenuState = async (
  restaurantName: string,
): Promise<MenuStateResponse> => {
  const response = await api.get<MenuStateResponse>("/menu-state", {
    params: { restaurant_name: restaurantName },
  });
  if (!response.config.edgeLocal && !response.config.edgeRetried)
    noteLunchServerTime(response.data?.serverTime);
  return response.data;
};

/** Вид на съставката — литералите са договор с BE (cross-repo-contract, ред 39). */
export type RecipeIngredientKind = "product" | "recipe";

/** Един ред от „Съставки" в „Инфо" (само в режим със склад). */
export interface RecipeIngredientLine {
  kind: RecipeIngredientKind;
  /** products.id при "product", recipes.id при "recipe" (под-рецепта) */
  id: number;
  name: string;
  /** Decimal — JSON НИЗ; за ЕДНА порция (под-рецепта: колко от нея влиза) */
  quantity: string;
  /** Мерната единица на продукта; под-рецептата няма */
  unit: string | null;
  /** units_of_measure.decimal_places; null — под-рецепта или продукт без единица */
  decimalPlaces: number | null;
}

/**
 * Съставките на рецептата на ЕДНО ниво (под-рецептата е един ред). Без склад
 * BE връща []. Пътят е под огледалния префикс `/menu/` (lib/edge/edgeMirror.ts),
 * затова без интернет касата сервира последния отговор за същата рецепта или 503.
 */
export const getRecipeIngredientLines = async (
  recipeId: number,
  language: string,
): Promise<RecipeIngredientLine[]> => {
  const response = await api.get<RecipeIngredientLine[]>(
    `/menu/recipes/${recipeId}/ingredients`,
    { params: { language } },
  );
  return response.data;
};
